import { useEffect, useState } from "react";
import { listRenewals, exitCustomer } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DoorOpen, LogOut } from "lucide-react";
import { logRenewal } from "@/lib/renewal";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monthRange = (offset) => {
  const now = new Date();
  return [iso(new Date(now.getFullYear(), now.getMonth() + offset, 1)), iso(new Date(now.getFullYear(), now.getMonth() + offset + 1, 0))];
};
const waLink = (phone) => {
  const d = (phone || "").replace(/\D/g, "");
  return d.length >= 10 ? `https://wa.me/${d.length === 10 ? `91${d}` : d}` : null;
};

/**
 * Members · Up for renewal: members whose membership ended recently or ends soon (default), or ends
 * in a month / date range (CR-0076). Each row shows the member manager and the last reach-out, with
 * call / WhatsApp, Log renewal and Log exit, so the list is worked in FPA rather than exported.
 * `initial` = the default view, pre-fetched by the parent; `q` and `coach` filter it like the Members tab.
 * `onCustomerExited(key)` fires after a successful exit so the parent can refresh its list.
 */
export default function RenewalNudgePanel({ initial, q = "", coach = "all", onCustomerExited }) {
  const [range, setRange] = useState("default");           // default | this_month | next_month | custom
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(initial || null);
  const [exiting, setExiting] = useState(null); // renewal row being exited
  const params = range === "this_month" ? monthRange(0) : range === "next_month" ? monthRange(1)
    : range === "custom" ? [custom.start, custom.end] : null;
  const load = () => {
    if (range === "custom" && !(custom.start && custom.end)) return;
    listRenewals(params ? { start: params[0], end: params[1] } : undefined)
      .then(setData).catch(() => setData({ window_days: 30, items: [] }));
  };
  useEffect(() => {
    if (range === "default" && initial) { setData(initial); return; }
    load();
  }, [range, custom.start, custom.end, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = data?.window_days || 30;
  const items = (data?.items || []).filter((r) => (coach === "all" || (r.member_manager || "").trim() === coach)
    && (!q || [r.name, r.phone, r.member_manager].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))));
  const overdue = items.filter((i) => i.overdue).length;
  const upcoming = items.length - overdue;
  const fmt = (d) => new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  const chip = (key, label) => (
    <button key={key} onClick={() => setRange(key)} data-testid={`renewal-range-${key}`}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${range === key ? "border-rose-500/50 bg-rose-500/10 text-rose-300" : "border-[#262626] text-zinc-400 hover:border-rose-500/30"}`}>
      {label}
    </button>
  );

  return (
    <section data-testid="notifications-section">
      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="renewal-range">
        {chip("default", `Ended last 60 days + next ${days}`)}
        {chip("this_month", "Ending this month")}
        {chip("next_month", "Ending next month")}
        {chip("custom", "Pick dates")}
        {range === "custom" && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400">
            <Input type="date" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
              className="h-8 w-[150px] bg-[#0a0a0a] border-[#262626] text-zinc-100" data-testid="renewal-range-start" />
            to
            <Input type="date" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
              className="h-8 w-[150px] bg-[#0a0a0a] border-[#262626] text-zinc-100" data-testid="renewal-range-end" />
          </span>
        )}
        <div className="ml-auto text-[11px] text-zinc-500">
          <span className="text-rose-400 font-medium">{overdue}</span> ended · <span className="text-amber-400 font-medium">{upcoming}</span> upcoming
        </div>
      </div>
      <div className="card-surface overflow-hidden">
        {!data ? (
          <div className="px-4 py-14 text-center text-sm text-zinc-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-14 text-center text-sm text-zinc-500">
            {range === "custom" && !(custom.start && custom.end) ? "Pick a start and end date." : "No memberships end in this period."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
                  <th className="px-4 py-3 font-medium">Member</th>
                  <th className="px-4 py-3 font-medium">Programme</th>
                  <th className="px-4 py-3 font-medium">End date</th>
                  <th className="px-4 py-3 font-medium">Member manager</th>
                  <th className="px-4 py-3 font-medium">Last reach-out</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.key} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]" data-testid={`renewal-row-${r.key}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-100">{r.name || "—"}</div>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                        {r.phone ? <a href={`tel:${r.phone}`} className="hover:text-zinc-300">{r.phone}</a> : "—"}
                        {waLink(r.phone) && <a href={waLink(r.phone)} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300" data-testid={`renewal-wa-${r.key}`}>WhatsApp</a>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300 text-xs">{r.programme || "—"}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <div className="text-zinc-400">{fmt(r.end_date)}</div>
                      <div className={r.overdue ? "text-rose-300" : "text-amber-300"}>
                        {r.overdue ? `ended ${Math.abs(r.days_to_end)}d ago` : r.days_to_end === 0 ? "ends today" : `in ${r.days_to_end}d`}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300 text-xs">{r.member_manager || <span className="text-zinc-600">None</span>}</td>
                    <td className="px-4 py-3 text-xs max-w-[240px]" data-testid={`renewal-contact-${r.key}`}>
                      {r.last_contact ? (
                        <>
                          <div className="text-zinc-400">{fmt(r.last_contact.date)}{r.last_contact.reachout_type ? ` · ${r.last_contact.reachout_type}` : ""}{r.last_contact.by ? ` · ${r.last_contact.by}` : ""}</div>
                          <div className="text-zinc-500 truncate" title={r.last_contact.note}>{r.last_contact.note}</div>
                        </>
                      ) : <span className="text-zinc-600">Not contacted</span>}
                      {r.non_renewal_reason && <div className="text-rose-300/80 truncate" title={r.non_renewal_reason}>Not renewing: {r.non_renewal_reason}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          onClick={() => logRenewal(r)}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-medium border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
                          data-testid={`log-renewal-btn-${r.key}`}
                        >
                          Log renewal
                        </button>
                        <button
                          onClick={() => setExiting(r)}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-medium border border-rose-500/50 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition-colors"
                          data-testid={`log-exit-btn-${r.key}`}
                        >
                          <DoorOpen className="h-3 w-3" /> Log exit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {exiting && (
        <ExitDialog
          row={exiting}
          onClose={() => setExiting(null)}
          onSaved={() => {
            const key = exiting.key;
            setExiting(null);
            load();
            if (onCustomerExited) onCustomerExited(key);
          }}
        />
      )}
    </section>
  );
}

export function ExitDialog({ row, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error("Exit reason is required");
    setBusy(true);
    try {
      await exitCustomer(row.key, { exit_reason: reason.trim(), customer_name: row.name });
      toast.success(`${row.name || "Member"} moved to Exited`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[#0a0a0a] border-[#1f1f1f] text-zinc-100" data-testid="exit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogOut className="h-4 w-4 text-rose-400" />
            Log exit — {row.name}
          </DialogTitle>
        </DialogHeader>
        <form data-testid="renewal-nudge-panel-form-152" onSubmit={submit} className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Reason for exit *</div>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this member leaving? (e.g. Cost concern / Relocation / Injury / Not satisfied)"
              rows={3}
              className="bg-[#0a0a0a] border-[#262626]"
              data-testid="exit-reason-input"
              autoFocus
            />
            <div className="text-[10px] text-zinc-500 mt-1">
              Once saved, this member moves into the <span className="text-zinc-300">Exited</span> tab and is hidden from the active list.
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="renewal-nudge-panel-button-169" type="button" variant="outline" onClick={onClose} className="border-[#262626] bg-transparent hover:bg-[#141414]">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-rose-600 hover:bg-rose-500 rounded-full" data-testid="exit-confirm-btn">
              {busy ? "Saving…" : "Confirm exit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
